import { useState, useCallback, useEffect } from "react";
import {
    Course,
    CourseDetail,
    EventActions,
    EventCategories,
    EventNames,
    FBCourseStats,
    PurchasableGift,
    ReceivedGift,
} from "JS/Models";
import { sortArrayByKey } from "JS/Helpers";
import { getErrorMessage } from "JS/Helpers/UserMessaging";
import { useSnackbar } from "notistack";
import { useAppDispatch, useAppSelector } from "JS/Redux/Store";
import { CoursesService } from "JS/Services/Courses";
import {
    ActiveCourse,
    setActiveCourse,
    setGlobalCourseGiftsGiven,
    setGlobalCourses,
    setGlobalPurchasableCourses,
    setGlobalPurchasedCourses,
    setGlobalReceivedCourses,
} from "JS/Redux/Course";
import moment from "moment";
import cloneDeep from "lodash/cloneDeep";
import { CourseLogFirebaseEventSettings } from "../../Scenes/Courses/Steps/CourseStep";
import { useSyncOfflineCourses } from "./SyncOffline";
import { useGlobalMediaEssentials } from "../MediaEssentials";
import { GiftService } from "JS/Services/Gift";
import { useContentUpdate } from "../ContentUpdate";

export const service = new CoursesService();
export const giftService = new GiftService();

export const useCourses = () => {
    const [loading, setLoading] = useState(false);
    const [coursesLocal, setCoursesLocal] = useState<Course[]>([]);
    const { enqueueSnackbar } = useSnackbar();
    const { mediaEssentials } = useGlobalMediaEssentials();
    const { courses, cacheTime, setGlobalCourses } = useGlobalCourses();
    const { syncOfflineCourses } = useSyncOfflineCourses();
    const { fetch: fetchContentUpdate } = useContentUpdate();

    const refetch = useCallback(() => {
        setLoading(true);

        return service
            .getCourses()
            .then((res) => {
                const allCourses: Course[] = res.data.map((item) => ({
                    ...item,
                    isReceived: false,
                }));
                const toSave = sortArrayByKey(allCourses, "title", "ASC");
                setCoursesLocal(toSave);
                setGlobalCourses(toSave);
                syncOfflineCourses(allCourses, "Course");
                return res;
            })
            .catch((err) => {
                enqueueSnackbar(err.message, {
                    variant: "error",
                });
                return {
                    data: [] as Course[],
                    status: false,
                    message: err.message,
                };
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        fetchContentUpdate("Course", cacheTime).then((r) => {
            if (mediaEssentials?.allow_rule_engine_requests && r) refetch();
        });
    }, []);

    return {
        refetch,
        loading,
        courses,
        coursesLocal,
    };
};

export const usePurchasableCourses = (skip?: boolean) => {
    const [loading, setLoading] = useState(false);
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const allCoursesState = useAppSelector((state) => state.courses);

    const refetch = useCallback(() => {
        setLoading(true);
        return giftService
            .getPurchasableGifts("GiftCourse")
            .then(async (response) => {
                dispatch(
                    setGlobalPurchasableCourses({
                        purchasableCoursesForMembers: sortArrayByKey(
                            response.data,
                            "title",
                            "ASC",
                        ),
                    }),
                );

                return response;
            })
            .catch((err) => {
                enqueueSnackbar(err.message, {
                    variant: "error",
                });
                return {
                    data: [] as PurchasableGift[],
                    status: false,
                    message: err.message,
                };
            })

            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if (!skip) refetch();
    }, [skip]);

    return {
        refetch,
        loading,
        courses: allCoursesState.courses,
    };
};

export const usePurchasedGiftCourses = (skip?: boolean) => {
    const [loading, setLoading] = useState(false);
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const allCoursesState = useAppSelector((state) => state.courses);
    const refetch = useCallback(async () => {
        setLoading(true);

        return service
            .getPurchasedGiftCourses()
            .then(async (val) => {
                dispatch(
                    setGlobalPurchasedCourses({
                        ...allCoursesState,
                        coursesToSend: val.data,
                    }),
                );

                return val.data;
            })
            .catch((err) => {
                enqueueSnackbar(getErrorMessage(err), {
                    variant: "error",
                });
                return [];
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if (!skip) refetch();
    }, [skip]);

    return {
        refetch,
        loading,
        courses: allCoursesState.courses,
    };
};

export const useReceivedCourses = (skip: boolean = false) => {
    const [loading, setLoading] = useState(false);
    const [receivedLocal, setReceivedLocal] = useState<ReceivedGift[]>([]);

    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const allCoursesState = useAppSelector((x) => x.courses);
    const { syncOfflineCourses } = useSyncOfflineCourses();

    const getGiftDate = (nid: string) =>
        allCoursesState?.receivedCourses
            ?.find((c) => c.nid === nid)
            ?.sender?.sending_date_time?.toString()
            ?.split(" ")[0];

    const refetch = useCallback(() => {
        setLoading(true);

        return giftService
            .getReceivedGifts("GiftCourse")
            .then((response) => {
                const allCourses = response?.data?.map((item) => ({
                    ...item,
                    isReceived: true,
                }));
                const toSave = sortArrayByKey(allCourses, "title", "ASC");
                setReceivedLocal(toSave);

                dispatch(
                    setGlobalReceivedCourses({
                        receivedCourses: toSave,
                    }),
                );
                syncOfflineCourses(allCourses, "GiftCourse");

                return response;
            })
            .catch((err) => {
                enqueueSnackbar(err.message, {
                    variant: "error",
                });
                return {
                    data: [] as ReceivedGift[],
                    status: false,
                    message: err.message,
                };
            })

            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if (!skip) refetch();
    }, [skip]);

    return {
        refetch,
        loading,
        receivedLocal,
        receivedCourses: allCoursesState.receivedCourses,
        getGiftDate,
    };
};

export const useCourseDetail = (
    received: boolean,
    activeCourse: Course | ReceivedGift,
    contentType: "Course" | "GiftCourse",
) => {
    const withMedia =
        received ||
        (!received &&
            ((activeCourse as Course)?.is_purchased ||
                (activeCourse as Course)?.publish_free === "1"));

    const detailWithMedia = useCourseDetailWithMedia(
        withMedia ? activeCourse?.nid : null,
        contentType,
    );
    const detailWithOutMedia = useCourseDetailWithOutMedia(
        !withMedia ? activeCourse?.nid : null,
        contentType,
    );

    const { detail, loading: detailLoading } = withMedia
        ? detailWithMedia
        : detailWithOutMedia;

    return {
        detail,
        withMedia,
        courseNid: activeCourse?.nid,
        courseSku: activeCourse?.sku_id,
        detailLoading,
    };
};

export const useCourseDetailWithMedia = (
    nid: string = null,
    contentType: "Course" | "GiftCourse" = "Course",
) => {
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState<CourseDetail>(null);
    const { syncOfflineCourseSteps } = useSyncOfflineCourses();

    const refetch = useCallback((nid: string) => {
        setLoading(true);
        setDetail(null);

        return service
            .getCourseDetailWithMedia(nid)
            .then(async (res) => {
                if (res?.data) {
                    const toSave = {
                        ...res.data,
                        nid,
                        contentType,
                    };
                    setDetail(toSave);
                    syncOfflineCourseSteps(cloneDeep(toSave));
                }
                return res;
            })
            .catch((err) => {
                enqueueSnackbar(err.message, {
                    variant: "error",
                });
                return {
                    data: [] as CourseDetail[],
                    status: false,
                    message: err.message,
                };
            })

            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if (nid != null) refetch(nid);
    }, [nid]);

    return {
        refetch,
        detail,
        loading,
    };
};

export const useCourseDetailWithOutMedia = (
    nid: string = null,
    contentType: "Course" | "GiftCourse" = "Course",
) => {
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState<CourseDetail>(null);

    const refetch = useCallback((nid: string) => {
        setLoading(true);
        setDetail(null);

        return service
            .getCourseDetailWithoutMedia(nid)
            .then(async (res) => {
                if (res?.data) {
                    setDetail({ ...res.data, nid, contentType });
                }
                return res;
            })
            .catch((err) => {
                enqueueSnackbar(err.message, {
                    variant: "error",
                });
                return {
                    data: [] as CourseDetail[],
                    status: false,
                    message: err.message,
                };
            })

            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if (nid != null) refetch(nid);
    }, [nid]);

    return {
        refetch,
        detail,
        courseNid: nid,
        loading,
    };
};

export const useGlobalCourses = () => {
    const dispatch = useAppDispatch();

    const allCources = useAppSelector((state) => state.courses);

    return {
        setGlobalCourses: (courses: Course[]) => {
            dispatch(
                setGlobalCourses({
                    courses: courses,
                }),
            );
        },

        setGlobalReceivedCourses: (courses: ReceivedGift[]) => {
            dispatch(
                setGlobalReceivedCourses({
                    receivedCourses: courses,
                }),
            );
        },
        setGlobalPurchasableCourses: (courses) => {
            dispatch(
                setGlobalPurchasableCourses({
                    purchasableCoursesForMembers: courses,
                }),
            );
        },
        setGlobalPurchasedCourses: (courses) => {
            dispatch(
                setGlobalPurchasedCourses({
                    ...courses,
                }),
            );
        },
        setGlobalCourseGiftsGiven: (courses) => {
            dispatch(
                setGlobalCourseGiftsGiven({
                    giftsGiven: courses,
                }),
            );
        },
        courses: allCources?.courses,
        receivedCourses: allCources?.receivedCourses,
        purchasableCoursesForMembers: allCources?.purchasableCoursesForMembers,
        coursesToSend: allCources?.coursesToSend,
        giftsGiven: allCources?.giftsGiven,
        cacheTime: allCources?.cacheTime,
    };
};

export const useActiveCourse = () => {
    const dispatch = useAppDispatch();

    return {
        activeCourse: useAppSelector((state) => state.courses).activeCourse,
        setActiveCourse: (course: ActiveCourse) => {
            dispatch(
                setActiveCourse({
                    type: course.type,
                    course: course.course,
                }),
            );
        },
    };
};

export const useCourseCompletion = (courseType: string) => {
    async function updateCourseCompletion(
        latestCourseStats: FBCourseStats,
        updateCourseStats: (
            stats: FBCourseStats,
            refetch?: boolean,
        ) => Promise<void>,
        handleLogFirebaseEvents: (
            type: "course" | "step" | "question",
            settings: CourseLogFirebaseEventSettings,
        ) => void,
    ) {
        if (latestCourseStats) {
            let allStepsCompletedCount = [];

            if (latestCourseStats?.steps) {
                for (const key of Object.keys(latestCourseStats.steps)) {
                    allStepsCompletedCount.push(
                        latestCourseStats?.steps[key].analytics
                            ?.completed_count,
                    );
                }
            }

            const completedSteps = allStepsCompletedCount?.filter(
                (n) => n > 0,
            )?.length;

            const courseCompletionCount = Math.min(...allStepsCompletedCount);

            if (
                latestCourseStats.analytics.completed_count <
                    courseCompletionCount &&
                completedSteps === latestCourseStats?.analytics?.total_steps
            ) {
                if (courseType === "Course") {
                    handleLogFirebaseEvents("course", {
                        action: EventActions.COMPLETED,
                        category: EventCategories.COURSE,
                        eventNames: EventNames.COURSE_COMPLETED,
                    });
                }
                if (courseType === "GiftCourse") {
                    handleLogFirebaseEvents("course", {
                        action: EventActions.COMPLETED,
                        category: EventCategories.GIFT_COURSE,
                        eventNames: EventNames.GIFT_COURSE_COMPLETED,
                    });
                }
            }

            const completedCount =
                completedSteps === latestCourseStats.analytics.total_steps
                    ? courseCompletionCount
                    : 0;

            const coursePercentage = +(
                (completedSteps / latestCourseStats?.analytics?.total_steps) *
                100
            ).toFixed(0);

            latestCourseStats.analytics.last_completed_date =
                completedCount === 0 ? 0 : moment().valueOf();

            latestCourseStats.analytics.completed_count = completedCount;

            if (
                !latestCourseStats.analytics.first_completed_date ||
                completedCount === 0
            ) {
                latestCourseStats.analytics.first_completed_date =
                    completedCount === 0 ? 0 : moment().valueOf();
            }

            const computeProgressState = () => {
                if (completedSteps === 0 || !latestCourseStats?.steps) {
                    return 0;
                } else {
                    return completedSteps ===
                        latestCourseStats.analytics.total_steps
                        ? 2
                        : 1;
                }
            };

            await updateCourseStats(
                {
                    ...latestCourseStats,
                    last_updated: moment().valueOf(),
                    analytics: {
                        ...latestCourseStats.analytics,
                        progress_state: computeProgressState(),
                        completed_steps: completedSteps,
                        completed_percentage: coursePercentage,
                    },
                },
                true,
            );
        }
    }

    return {
        updateCourseCompletion,
    };
};
